(() => {
    useNavFactory.$inject = ['$state'];

    function useNavFactory($state) {
        return function ($scope) {

            const state = {
                activeNavs: [[]]
            };

            const toggleDropdown = (item) => {
                item.dropdownOpen = !item.dropdownOpen;
            };

            const closeDropdowns = (activeNavs) => {
                for (const nav of activeNavs) {
                    for (const section of nav.filteredSections) {
                        for (const item of section.items) {
                            if (item.dropdownOpen) {
                                item.dropdownOpen = false;
                            }
                        }
                    }
                }
            };

            const isSubItemActive = (function () {
                const memo = new Map();
                let lastState;

                return (navs) => {
                    if (!$state.current.name.startsWith(lastState)) {
                        memo.clear();
                    }

                    if (!$state.current.name.startsWith(lastState) || !memo.has(navs)) {
                        lastState = $state.current.name;
                        let isFound = false;

                        for (let nav of navs) {
                            for (let section of nav.sections) {
                                for (let item of section.items) {
                                    if (item.isActive?.()) {
                                        isFound = true;
                                        break;
                                    }

                                    if (item.subNav && isSubItemActive(item.subNav)) {
                                        isFound = true;
                                        break;
                                    }
                                }
                            }
                        }

                        memo.set(navs, isFound);
                    }

                    return memo.get(navs);
                };
            })();

            const focusOnActiveItem = (navIndex = 0) => {
                for (let navMenu of state.activeNavs[navIndex]) {
                    for (let section of navMenu.sections) {
                        const activeItemWithNav = section.items.find(item => item.subNav && isSubItemActive(item.subNav));
                        if (activeItemWithNav) {
                            state.activeNavs[navIndex + 1] = activeItemWithNav.subNav;
                            focusOnActiveItem(navIndex + 1);
                            break;
                        }
                    }
                }
            };

            const init = (initialNav) => {
                return prepare(initialNav)
                    .then(nav => {
                        state.activeNavs = [nav];
                        $scope?.$digest();
                    });
            }

            const getNavItemsVisibilityPromises = (nav) => {
                const promises = [];

                const checkVisibility = (item) => {
                    if (item.isVisible) {
                        promises.push(
                            item.isVisible()
                                .then((visible) => {
                                    item.visible = visible;
                                })
                        );
                    }
                };

                nav.sections?.forEach((section) => {
                    checkVisibility(section);
                    section.items?.forEach(item => {
                        checkVisibility(item);
                        item.subItems?.forEach(checkVisibility);

                        if (item.subNav) {
                            promises.push(...getNavItemsVisibilityPromises(item.subNav));
                        }
                    });
                });

                return promises;
            };

            const filterInvisibleItems = async (items) => {
                let resultItems = [];

                const promises = items.map((item, index) => {
                    if (item.isVisible) {
                        return item.isVisible()
                            .then((visible) => {
                                if (visible) {
                                    resultItems[index] = item;
                                }
                            })
                            .catch((err) => {
                                console.error(err);
                            })
                    } else if (item.subItems) {
                        return filterInvisibleItems(item.subItems)
                            .then((subItems) => {
                                if (subItems.length) {
                                    resultItems[index] = item;
                                }
                            });
                    }

                    resultItems[index] = item;
                });

                await Promise.all(promises);
                return resultItems.filter((item) => !!item);
            };

            const sortItems = (items) => {
                const resultItems = [...items];
                return resultItems.sort((item1, item2) => item1.title.localeCompare(item2.title, undefined, {sensitivity: "base"}));
            };

            const prepare = async (navs) => {
                navs = await filterInvisibleItems(navs);
                await Promise.all(navs.map((nav, index) => {
                    nav.promise = filterInvisibleItems(nav.sections)
                        .then((sections) => {
                            const navSections = navs[index].sortable ? sortItems(sections) : sections;
                            navs[index] = {
                                ...nav,
                                sections: navSections
                            };

                            return Promise.all(navSections.map((section, sectionIndex) => {
                                return filterInvisibleItems(section.items)
                                    .then((items) => {
                                        const sectionItems = navs[index].sections[sectionIndex].sortable ? sortItems(items) : items;
                                        navs[index].sections[sectionIndex] = {
                                            ...section,
                                            items: sectionItems
                                        }

                                        sectionItems.forEach((item, itemIndex) => {
                                            if (item.subNav) {
                                                prepare(item.subNav)
                                                    .then((subNav) => {
                                                        const activeIndex = state.activeNavs.indexOf(item.subNav);

                                                        navs[index].sections[sectionIndex].items[itemIndex] = {
                                                            ...item,
                                                            subNav
                                                        };

                                                        if (activeIndex > -1) {
                                                            state.activeNavs[activeIndex] = subNav;
                                                        }
                                                    })
                                                    .finally(() => {
                                                        $scope?.$digest();
                                                    });
                                            }

                                            if (item.getBadgeContent) {
                                                item.getBadgeContent()
                                                    .then((badgeContent) => {
                                                        item.badgeContent = badgeContent;
                                                        $scope?.$digest();
                                                    });
                                            }

                                            if (item.subItems) {
                                                return filterInvisibleItems(item.subItems)
                                                    .then((subItems) => {
                                                        navs[index].sections[sectionIndex].items[itemIndex] = {
                                                            ...item,
                                                            subItems
                                                        };
                                                    });
                                            }
                                        });
                                    });
                            }));
                        })
                        .then(() => {
                            navs[index].ready = true;
                            navs[index].filteredSections = [...navs[index].sections];
                        });

                    return nav.promise;
                }));

                return navs;
            };

            const toggleSearch = (nav) => {
                nav.search.showInput = !nav.search.showInput;

                if (!nav.search.showInput) {
                    nav.search.term = '';
                    filterItems(nav);
                }
            };

            const filterItems = (nav, searchTerm) => {
                nav.filteredSections = nav.sections
                    .map(section => ({...section}))
                    .filter((section) => {
                        if (!searchTerm || !nav.search.showInput) {
                            return true;
                        }

                        section.items = section.items.filter((item) => {
                            return item.title.toLowerCase().includes(searchTerm.toLowerCase());
                        });

                        return section.items.length > 0;
                    });

                nav.noSearchResult = !nav.filteredSections.some((section) => section.items.length);
            };

            const openSubNav = (nav, level) => {
                if (!state.activeNavs.includes(nav)) {
                    state.activeNavs.length = level;
                    state.activeNavs.push(nav);
                }
            };

            const closeSubNav = (nav) => {
                const navIndex = state.activeNavs.indexOf(nav);

                if (navIndex > -1) {
                    state.activeNavs.splice(navIndex, 1);
                }
            };

            return {
                state,
                init,
                toggleSearch,
                toggleDropdown,
                closeDropdowns,
                openSubNav,
                closeSubNav,
                filterItems,
                isSubItemActive,
                focusOnActiveItem,
                prepare,
            };
        }
    }

    angular
        .module("harriApp")
        .factory('useNavFactory', useNavFactory);
})();